import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/useAuthStore";
import ProfileLoadError from "./components/ProfileLoadError";
import { useSignOut } from "./hooks/useSignOut";
import { useTokenRefresh } from "./hooks/useTokenRefresh";
import { establishCurrentUser } from "./utils/establishCurrentUser";
import { isAccessTokenExpired } from "./utils/isAccessTokenExpired";
import { resyncOutageNotificationsForCurrentUser } from "../../stores/useOutageNotificationStore";

type RequireAuthProps = {
  children: ReactNode;
};

type BootStatus = "booting" | "ready" | "profile-error";

export default function RequireAuth({ children }: RequireAuthProps) {
  const location = useLocation();
  const { refresh, isRefreshing } = useTokenRefresh();
  const { signOut } = useSignOut();
  const user = useAuthStore((state) => state.user);
  const [status, setStatus] = useState<BootStatus>("booting");
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      // Read live store values inside the effect — avoids stale closures
      // and means we don't need accessToken as a dep
      let { accessToken } = useAuthStore.getState();

      // A present, non-expired access token means the session is already
      // valid — no need to touch the network for a refresh.
      if (!accessToken || isAccessTokenExpired(accessToken)) {
        // No client-readable signal exists for whether a refresh session is
        // active (the refresh token lives only in an httpOnly cookie), so
        // silent refresh is always attempted here. A missing/expired cookie
        // resolves to `false` and falls through to the "no accessToken →
        // redirect to /sign-in" render path below — this is the same path
        // for "session expired" and "never logged in".
        await refresh();
        accessToken = useAuthStore.getState().accessToken;
      }

      // `user` is only ever persisted to this tab's own sessionStorage, never
      // to anything shared across tabs (see useAuthStore) — a fresh tab (or
      // one whose stored user is missing/corrupt/incomplete) always resolves
      // it from its OWN session via /users/me, never from another tab's
      // cached identity or from token claims.
      if (accessToken && !useAuthStore.getState().user) {
        const result = await establishCurrentUser();
        if (cancelled) return;
        if (result.status === "failed") {
          setStatus("profile-error");
          return;
        }
        if (result.status === "ok") {
          // persist only rehydrates once at module load, before this tab's
          // user was known — re-sync now so the notification store reads
          // from this user's own storage key, not a stale/default one
          // (JAA-1096).
          await resyncOutageNotificationsForCurrentUser();
        }
      }

      if (!cancelled) setStatus("ready");
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [refresh]); // refresh is the only external dep — stable ref from useTokenRefresh

  const handleRetry = useCallback(async (): Promise<void> => {
    setIsRetrying(true);
    const result = await establishCurrentUser();
    setIsRetrying(false);
    if (result.status !== "failed") setStatus("ready");
  }, []);

  // Never redirect while silent refresh is in progress
  if (status === "booting" || isRefreshing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Read live state after boot completes — not the stale closure
  // value captured before refresh ran
  const currentAccessToken = useAuthStore.getState().accessToken;
  if (!currentAccessToken) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  // Children only ever render with a real identity — rendering them with no
  // user is what used to surface role-less 404s and placeholder identities.
  if (status === "profile-error" || !user) {
    return (
      <ProfileLoadError
        onRetry={() => void handleRetry()}
        onSignOut={() => void signOut()}
        isRetrying={isRetrying}
      />
    );
  }

  return <>{children}</>;
}
